import { TransactionListItem } from "@common/schemas/transaction.schema";
import { monetaryValuesEqual } from "@common/helpers/monetary";
import { FiltersTypes } from "@common/types/filters/general";
import {
    AccrualMatchSuggestionDisplay,
    getFirstDocumentSuggestion,
    getTransferSuggestion,
} from "@common/schemas/domains/accrualReview/accrual-match-suggestion-display.schema";
import {
    ApproveAccrualReviewBody,
    BulkApproveAccrualReviewBody,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import type { AccountOverrideEntryRequest } from "@common/schemas/domains/accounting/account-override.schema";

interface ClassifyResult {
    acceptableTransactionIds: number[];
    reviewRequiredCount: number;
    alreadyAcceptedCount: number;
}

export function classifyTransactions({
    transactions,
    getSuggestions,
}: {
    transactions: TransactionListItem[];
    getSuggestions: (
        transaction: TransactionListItem,
    ) => AccrualMatchSuggestionDisplay[];
}): ClassifyResult {
    const acceptableTransactionIds: number[] = [];
    let reviewRequiredCount = 0;
    let alreadyAcceptedCount = 0;

    for (const transaction of transactions) {
        if (transaction.accrualAccountId) {
            alreadyAcceptedCount++;
            continue;
        }
        if (
            isTransactionReviewRequired(
                transaction,
                getSuggestions(transaction),
            )
        ) {
            reviewRequiredCount++;
        } else {
            acceptableTransactionIds.push(transaction.id);
        }
    }

    return {
        acceptableTransactionIds,
        reviewRequiredCount,
        alreadyAcceptedCount,
    };
}

export function buildBulkRequestBody({
    transactionIds,
    transactionsById,
    getProposedAccountId,
    getProposedAccountOverrides,
}: {
    transactionIds: number[];
    transactionsById: Map<number, TransactionListItem>;
    getProposedAccountId: (transaction: TransactionListItem) => string | null;
    getProposedAccountOverrides?: (
        transaction: TransactionListItem,
    ) => AccountOverrideEntryRequest[];
}): BulkApproveAccrualReviewBody {
    const accountOverrides: Record<
        string,
        string | AccountOverrideEntryRequest[]
    > = {};
    for (const id of transactionIds) {
        const tx = transactionsById.get(id);
        if (tx) {
            const proposedOverrides = getProposedAccountOverrides?.(tx) ?? [];
            if (proposedOverrides.length > 0) {
                accountOverrides[String(id)] = proposedOverrides;
            } else {
                const proposedId = getProposedAccountId(tx);
                if (proposedId) {
                    accountOverrides[String(id)] = proposedId;
                }
            }
        }
    }
    return {
        transactionIds,
        accountOverrides:
            Object.keys(accountOverrides).length > 0
                ? accountOverrides
                : undefined,
    };
}

/**
 * Build a filter-based bulk approve payload for the client-driven drain:
 * the UI repeatedly sends `{ filters, page: 0, limit }` until the server
 * returns zero processed rows (because approving a row flips it out of the
 * `isNotAccrualReviewed: true` filter).
 *
 * `overrides` carries every account override the user set in the
 * AccrualReviewProvider context; the server applies the entries whose
 * transaction ids happen to be in the page it loads and ignores the rest.
 */
export function buildBulkRequestBodyForFilters({
    filters,
    limit,
    overrides,
    getAccountOverrides,
}: {
    filters: FiltersTypes;
    limit: number;
    overrides: ReadonlyMap<number, ReadonlyMap<string, string>>;
    getAccountOverrides: (
        transactionId: number,
        accountsByLedger: ReadonlyMap<string, string>,
    ) => string | AccountOverrideEntryRequest[];
}): BulkApproveAccrualReviewBody {
    const accountOverrides: Record<
        string,
        string | AccountOverrideEntryRequest[]
    > = {};
    for (const [transactionId, accountsByLedger] of overrides) {
        const overrideValue = getAccountOverrides(
            transactionId,
            accountsByLedger,
        );
        if (typeof overrideValue === "string" || overrideValue.length > 0) {
            accountOverrides[String(transactionId)] = overrideValue;
        }
    }
    return {
        filters,
        page: 0,
        limit,
        accountOverrides:
            Object.keys(accountOverrides).length > 0
                ? accountOverrides
                : undefined,
    };
}

export function isTransactionReviewRequired(
    transaction: TransactionListItem,
    suggestions: AccrualMatchSuggestionDisplay[],
): boolean {
    return isAccrualMatchSuggestionReviewRequired({
        transactionAmount: transaction.amount,
        suggestions,
    });
}

export function isAccrualMatchSuggestionReviewRequired({
    transactionAmount,
    suggestions,
}: {
    transactionAmount: number;
    suggestions: AccrualMatchSuggestionDisplay[];
}): boolean {
    if (suggestions.length > 1) {
        return true;
    }

    for (const suggestion of suggestions) {
        const doc = suggestion.invoice ?? suggestion.bill;
        if (doc?.openAmount != null) {
            const isPartialDocMatch = !monetaryValuesEqual(
                doc.openAmount,
                Math.abs(transactionAmount),
            );
            if (isPartialDocMatch) {
                return true;
            }
        }

        if (suggestion.matchedTransaction) {
            const isPartialTransfer = !monetaryValuesEqual(
                Math.abs(suggestion.matchedTransaction.amount),
                Math.abs(transactionAmount),
            );
            if (isPartialTransfer) {
                return true;
            }
        }
    }

    return false;
}

export enum AccrualReviewActionKind {
    ACCEPT = "accept",
    OPEN_MATCH_DRAWER = "openMatchDrawer",
}

interface GetAccrualReviewActionDecisionParams {
    hasActiveMatchSuggestion: boolean;
    needsReview: boolean;
    ruleAccountIsActionable: boolean;
}

export function getAccrualReviewActionDecision({
    hasActiveMatchSuggestion,
    needsReview,
    ruleAccountIsActionable,
}: GetAccrualReviewActionDecisionParams): {
    action: AccrualReviewActionKind;
    label: "Confirm" | "Match" | "Review";
} {
    if (!ruleAccountIsActionable) {
        return {
            action: AccrualReviewActionKind.OPEN_MATCH_DRAWER,
            label: "Review",
        };
    }

    if (hasActiveMatchSuggestion) {
        return needsReview
            ? {
                  action: AccrualReviewActionKind.OPEN_MATCH_DRAWER,
                  label: "Review",
              }
            : {
                  action: AccrualReviewActionKind.ACCEPT,
                  label: "Match",
              };
    }

    if (needsReview) {
        return {
            action: AccrualReviewActionKind.OPEN_MATCH_DRAWER,
            label: "Review",
        };
    }

    return {
        action: AccrualReviewActionKind.ACCEPT,
        label: "Confirm",
    };
}

export function buildApproveBody({
    transaction,
    suggestions,
    proposedAccountId,
    primaryAccrualLedgerId,
    accountOverrides,
}: {
    transaction: TransactionListItem;
    suggestions: AccrualMatchSuggestionDisplay[];
    proposedAccountId: string | null;
    primaryAccrualLedgerId: string | null;
    accountOverrides?: AccountOverrideEntryRequest[];
}): ApproveAccrualReviewBody | null {
    const docSuggestion = getFirstDocumentSuggestion(suggestions);
    if (docSuggestion?.invoice) {
        return {
            transactionId: transaction.id,
            invoiceIds: [docSuggestion.invoice.id],
        };
    }
    if (docSuggestion?.bill) {
        return {
            transactionId: transaction.id,
            billIds: [docSuggestion.bill.id],
        };
    }

    const transfer = getTransferSuggestion(suggestions);
    if (transfer?.matchedTransaction) {
        return {
            transactionId: transaction.id,
            matchedTransactionId: transfer.matchedTransaction.id,
        };
    }

    if (accountOverrides && accountOverrides.length > 0) {
        return {
            transactionId: transaction.id,
            accountOverrides,
        };
    }

    if (!proposedAccountId || !primaryAccrualLedgerId) {
        return null;
    }

    return {
        transactionId: transaction.id,
        accountOverrides: [
            {
                accountingLedgerId: primaryAccrualLedgerId,
                accountId: proposedAccountId,
            },
        ],
    };
}
